/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React, { useEffect, useState } from 'react'
import { View, Text, BoxView, ActionButton } from '@components/atoms'
import {
	SeasonRegistrationModal,
	SeasonQueueOrderWarningModal,
} from '@components/molecules'
import { SeasonList, SeasonQueueList } from '@components/organisms'
import { Tabs, Tab, Chip } from '@mui/material'
import { WarningAmber } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'

import { useAppDispatch, useAppSelector, seasonActions } from '@redux/index'
import {
	SeasonProductAddType,
	SeasonProductBatchUpdateType,
	SeasonProductDeleteType,
	SeasonProductType,
	SeasonProductUpdateType,
	SeasonType,
} from '@schemas'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { SeasonProductList } from '@components/organisms'
import { green, grey, orange } from '@mui/material/colors'

const SeasonPage = () => {
	const dispatch = useAppDispatch()
	const { selectedSeason, selectedYear, seasonProducts, loading, products } =
		useAppSelector(state => state.season)
	const seasonList: SeasonType[] = useAppSelector(state => state.season.seasonList)
	const navigate = useNavigate()
	const [modalOpen, setModalOpen] = useState(false)
	const [isEditMode, setIsEditMode] = useState(false)
	const [currentSeasonProducts, setCurrentSeasonProducts] = useState<SeasonProductType[]>(
		[],
	)
	const [activeTab, setActiveTab] = useState<'list' | 'queue'>('list')
	// 대기열 추가 시 날짜 역전이 감지되면 확인을 받는다
	const [queueWarning, setQueueWarning] = useState<{
		target: SeasonType
		conflict: SeasonType
	} | null>(null)

	/**
	 * 지금 shop 에 노출중인 시즌.
	 *
	 * seasonList 는 선택 연도로 필터되므로 다른 연도를 보면 사라진다.
	 * 배너는 연도와 무관하게 고정돼야 하니 서버에서 따로 받은 값을 쓴다.
	 */
	const openSeason: SeasonType | null = useAppSelector(state => state.season.openSeason)

	/** 대기열 (queueOrder 오름차순) */
	const queueList: SeasonType[] = React.useMemo(
		() =>
			seasonList
				.filter(aSeason => aSeason.queueOrder !== null && aSeason.openYn !== 1)
				.sort((a, b) => (a.queueOrder ?? 0) - (b.queueOrder ?? 0)),
		[seasonList],
	)

	/**
	 * 판매중 시즌의 종료가 임박했는데 대기열이 비어 있으면 경고한다.
	 * 이번처럼 다음 시즌 없이 판매가 조용히 멈추는 것을 막기 위한 장치다.
	 */
	const endingSoonWarning: string | null = React.useMemo(() => {
		if (openSeason === null) {
			return '현재 판매 중지 상태입니다 — shop 의 전 상품이 Sold Out 으로 표시됩니다.'
		}

		const daysLeft = dayjs(openSeason.endDate)
			.startOf('day')
			.diff(dayjs().startOf('day'), 'day')

		if (daysLeft < 0) {
			return '종료일이 지난 시즌이 아직 판매중입니다. 다음 시즌으로 전환하세요.'
		}

		if (daysLeft <= 7 && queueList.length === 0) {
			return `${openSeason.title}이(가) ${dayjs(openSeason.endDate).format(
				'M월 D일',
			)} 종료됩니다. 다음 시즌을 준비하세요.`
		}

		return null
	}, [openSeason, queueList])

	useEffect(() => {
		if (seasonProducts) {
			setCurrentSeasonProducts(JSON.parse(JSON.stringify(seasonProducts)))
		}
	}, [seasonProducts])

	const handleSeasonClick = (season: SeasonType) => {
		dispatch(seasonActions.SET_SELECTED_SEASON(season))
		dispatch(seasonActions.FETCH_SEASON_PRODUCT(season.id))
	}
	const handleYearChange = (newValue: Date | null) => {
		if (newValue) {
			dispatch(seasonActions.SET_SELECTED_YEAR(dayjs(newValue).format('YYYY')))
		}
	}

	/**
	 * 대기열 추가.
	 *
	 * 앞 순번에 더 늦게 시작하는 시즌이 있으면(날짜 역전) 확인을 받는다.
	 * 역전이 없으면 그대로 맨 뒤에 붙인다. (SEASON_OPEN_PLAN.md §7.1)
	 */
	const handleAddToQueue = (season: SeasonType) => {
		const conflict = queueList.find(anExisting =>
			dayjs(anExisting.startDate).isAfter(dayjs(season.startDate)),
		)

		if (conflict) {
			setQueueWarning({ target: season, conflict: conflict })
			return
		}

		dispatch(
			seasonActions.UPDATE_SEASON_QUEUE([
				...queueList.map(aSeason => aSeason.id),
				season.id,
			]),
		)
		setActiveTab('queue')
	}

	/** 경고에도 그대로 맨 뒤에 추가 */
	const handleQueueKeepOrder = () => {
		if (queueWarning === null) return

		dispatch(
			seasonActions.UPDATE_SEASON_QUEUE([
				...queueList.map(aSeason => aSeason.id),
				queueWarning.target.id,
			]),
		)
		setQueueWarning(null)
		setActiveTab('queue')
	}

	/** 시작일 기준으로 올바른 위치에 삽입 */
	const handleQueueMoveForward = () => {
		if (queueWarning === null) return

		const target = queueWarning.target
		const insertIdx = queueList.findIndex(anExisting =>
			dayjs(anExisting.startDate).isAfter(dayjs(target.startDate)),
		)
		const newList = [...queueList]
		newList.splice(insertIdx === -1 ? newList.length : insertIdx, 0, target)

		dispatch(seasonActions.UPDATE_SEASON_QUEUE(newList.map(aSeason => aSeason.id)))
		setQueueWarning(null)
		setActiveTab('queue')
	}

	const handleOpenModal = () => {
		setModalOpen(true)
	}

	const handleCloseModal = () => {
		setModalOpen(false)
	}

	const handleEditMode = () => {
		if (isEditMode) {
			setIsEditMode(false)
			setCurrentSeasonProducts(JSON.parse(JSON.stringify(seasonProducts)))
		} else {
			setIsEditMode(true)
		}
	}

	const handleUpdateSeasonProduct = (
		updatedProducts: SeasonProductBatchUpdateType[],
		deletedProducts: SeasonProductType[],
	) => {
		const batchUpdatePayload: SeasonProductBatchUpdateType[] = updatedProducts.map(
			product => {
				if (product.iudFlag === 'I') {
					return {
						iudFlag: 'I',
						seasonId: selectedSeason?.id,
						productId: product.productId,
						usd: product.usd,
						discountUsd: product.discountUsd,
						quantity: product.quantity,
						activeYn: product.activeYn,
					} as SeasonProductAddType
				} else if (product.iudFlag === 'U') {
					return {
						iudFlag: 'U',
						id: product.id,
						seasonId: selectedSeason?.id,
						productId: product.productId,
						usd: product.usd,
						quantity: product.quantity,
						discountUsd: product.discountUsd,
						activeYn: product.activeYn,
						partOrderedAt: product.partOrderedAt,
						productionStartAt: product.productionStartAt,
						deliveryStartAt: product.deliveryStartAt,
						deliveryCompletedAt: product.deliveryCompletedAt,
					} as SeasonProductUpdateType
				} else {
					return {
						iudFlag: 'D',
						id: product.id,
					} as SeasonProductDeleteType
				}
			},
		)

		dispatch(seasonActions.BATCH_UPDATE_SEASON_PRODUCT([...batchUpdatePayload]))
	}

	return (
		<View css={style}>
			<View className="seasonOuterView">
				<BoxView
					title="시즌 리스트"
					className="seasonView"
					rightHeaderAccessory={
						<ActionButton
							title="시즌 등록"
							iconName="plus"
							buttonColor="primary"
							variant="contained"
							onClick={handleOpenModal}
						/>
					}
				>
					<View className="seasonContentView">
						{/* 지금 shop 에 노출중인 시즌을 목록 위에 고정해 한눈에 보이게 한다 */}
						<View className={`openBannerView ${openSeason ? 'open' : 'closed'}`}>
							<View className="openBannerHeader">
								<Chip
									label={openSeason ? '판매 중' : '판매 중지'}
									size="small"
									className="openBannerChip"
								/>
								<Text className="openBannerTitle">
									{openSeason ? openSeason.title : '판매중인 시즌 없음'}
								</Text>
							</View>

							{openSeason && (
								<Text className="openBannerDate">
									{`${dayjs(openSeason.startDate).format('YYYY-MM-DD')} ~ ${dayjs(
										openSeason.endDate,
									).format('YYYY-MM-DD')}`}
								</Text>
							)}

							{endingSoonWarning !== null && (
								<View className="openBannerWarning">
									<WarningAmber fontSize="small" />
									<Text className="openBannerWarningText">{endingSoonWarning}</Text>
								</View>
							)}
						</View>

						<Tabs
							value={activeTab}
							onChange={(_, newValue) => setActiveTab(newValue as 'list' | 'queue')}
							className="seasonTabs"
						>
							<Tab label="시즌 목록" value="list" />
							<Tab
								label={
									queueList.length > 0
										? `판매 대기열 (${queueList.length})`
										: '판매 대기열'
								}
								value="queue"
							/>
						</Tabs>

						<View className="seasonViewContentHeader">
							<LocalizationProvider dateAdapter={AdapterDateFns}>
								<DatePicker
									label="해당연도"
									value={dayjs(`${selectedYear as string}-01-01`).toDate()}
									onChange={handleYearChange}
									views={['year']}
									slotProps={{
										textField: {
											variant: 'outlined',
											size: 'small',
											style: {
												width: '8rem',
											},
										},
									}}
									format="yyyy-MM-dd"
									minDate={new Date('2025-01-01')}
								/>
							</LocalizationProvider>
						</View>
						<View className="listOuterView">
							{loading ? (
								<p>Loading...</p>
							) : activeTab === 'list' ? (
								<SeasonList
									seasons={seasonList}
									selectedSeasonId={selectedSeason === null ? '' : selectedSeason.id}
									onSeasonClick={handleSeasonClick}
									onAddToQueue={handleAddToQueue}
								/>
							) : (
								<View className="queueOuterView">
									<SeasonQueueList
										queueList={queueList}
										selectedSeasonId={selectedSeason === null ? '' : selectedSeason.id}
										onSeasonClick={handleSeasonClick}
										onOpenClick={aSeason => navigate(`/main/season/${aSeason.id}/edit`)}
									/>
								</View>
							)}
						</View>
					</View>
				</BoxView>
				<BoxView
					title={`시즌 상품 리스트 ${
						selectedSeason ? `- ${selectedSeason.title as string}` : ''
					}`}
					className="seasonProductView"
					rightHeaderAccessory={
						selectedSeason && (
							<ActionButton
								title={isEditMode ? '수정 종료' : '수정'}
								iconName={isEditMode ? 'cog-off' : 'cog'}
								buttonColor="primary"
								variant="contained"
								onClick={handleEditMode}
							/>
						)
					}
				>
					{selectedSeason ? (
						<SeasonProductList
							products={products}
							seasonProducts={currentSeasonProducts}
							isEditMode={isEditMode}
							onUpdateSeasonProduct={handleUpdateSeasonProduct}
						/>
					) : (
						<p>Select a season to view its products</p>
					)}
				</BoxView>
			</View>
			<SeasonRegistrationModal open={modalOpen} onClose={handleCloseModal} />

			<SeasonQueueOrderWarningModal
				open={queueWarning !== null}
				targetSeason={queueWarning?.target ?? null}
				conflictSeason={queueWarning?.conflict ?? null}
				onKeepOrder={handleQueueKeepOrder}
				onMoveForward={handleQueueMoveForward}
				onClose={() => setQueueWarning(null)}
			/>
		</View>
	)
}

export default SeasonPage

const style = css({
	'.seasonOuterView': {
		position: 'relative',
		display: 'flex',

		'.seasonView': {
			width: '30rem',
			marginRight: '0.5rem',
			paddingLeft: '2rem',

			'.seasonContentView': {
				display: 'flex',
				flexDirection: 'column',
				gap: '1rem',
				width: '100%',
				height: '100%',

				'.openBannerView': {
					padding: '0.75rem 1rem',
					marginTop: '0.5rem',
					borderRadius: '5px',
					border: '1px solid',

					'&.open': {
						background: '#f1f8f4',
						borderColor: green['A700'],

						'.openBannerChip': {
							background: green['A700'],
							color: 'white',
						},
					},

					'&.closed': {
						background: grey[100],
						borderColor: grey[400],

						'.openBannerChip': {
							background: grey[600],
							color: 'white',
						},
					},

					'.openBannerHeader': {
						display: 'flex',
						alignItems: 'center',
						gap: '0.5rem',

						'.openBannerChip': {
							fontWeight: 900,
							fontSize: '0.75rem',
						},

						'.openBannerTitle': {
							margin: 0,
							fontSize: '1.1rem',
							fontWeight: 700,
						},
					},

					'.openBannerDate': {
						display: 'block',
						margin: '0.25rem 0 0',
						color: grey[600],
						fontSize: '0.85rem',
					},

					'.openBannerWarning': {
						display: 'flex',
						alignItems: 'flex-start',
						gap: '0.35rem',
						marginTop: '0.5rem',
						color: orange[900],

						'.openBannerWarningText': {
							margin: 0,
							fontSize: '0.85rem',
							lineHeight: 1.5,
						},
					},
				},

				'.seasonTabs': {
					minHeight: '2.25rem',
					borderBottom: '1px solid',
					borderColor: grey[300],
					marginBottom: '-0.5rem',

					'.MuiTab-root': {
						minHeight: '2.25rem',
						fontWeight: 700,
					},
				},

				'.queueOuterView': {
					padding: '0.5rem 1rem',
				},

				'.seasonViewContentHeader': {
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'flex-end',
					width: '100%',
					marginBottom: '-0.5rem',
					paddingTop: '0.5rem',
				},

				'.listOuterView': {
					width: '100%',
					height: '100%',
					paddingTop: '5px',
					paddingBottom: '5px',
					border: '1px solid',
					borderColor: grey[300],
					borderRadius: '5px',
					backgroundColor: grey[50],
					overflowY: 'auto',
				},
			},
		},
		'.seasonProductView': {
			width: 'calc(100% - 30rem)',
		},
	},
})
